import { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../services/authService";
import { useTranslation } from "react-i18next";
import {
  FaUsersCog,
  FaMicrochip,
  FaSignOutAlt,
  FaClipboardList,
  FaFileAlt,
} from "react-icons/fa";
import {
  normalizeStatus,
  normalizeRole,
  canDownloadFirmware,
  canManageFirmware,
  canDownloadClientDocs,
  canManageClients,
} from "../../services/authz";
import "./Header.scss";

export default function Header() {
  const {
    currentUser,
    userRole,
    userStatus,
    loading,
    authTransitioning,
    logout,
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [animatingLinks, setAnimatingLinks] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Logout failed.");
    }
  };

  const status = useMemo(() => normalizeStatus(userStatus), [userStatus]);
  const role = useMemo(() => normalizeRole(userRole), [userRole]);

  const isResolvingAuth = loading || authTransitioning;

  const isOnHomePage = location.pathname === "/";
  const isOnLoginPage = location.pathname === "/login";
  const isOnRegisterPage = location.pathname === "/register";
  const isOnRegisteredPage = location.pathname === "/registered";

  const isPublicRoute =
    isOnHomePage || isOnLoginPage || isOnRegisterPage || isOnRegisteredPage;

  const isResolvedAuthenticated =
    !isResolvingAuth &&
    !!currentUser &&
    !!status &&
    !!role &&
    (status === "approved" || status === "active");

  const isResolvedPublic = !isResolvingAuth && !currentUser;

  const showPublicNav = isResolvedPublic && isPublicRoute;
  const showAppNav = isResolvedAuthenticated && !isPublicRoute;

  const capClientDocs = canDownloadClientDocs(status, role);
  const capFirmwareRead = canDownloadFirmware(status, role);
  const capClientsManage = canManageClients(status, role);
  const capFirmwareManage = canManageFirmware(status, role);

  const isOnDashboardPage = location.pathname.startsWith("/dashboard");

  const isOnFirmwarePage =
    location.pathname.startsWith("/firmware") ||
    location.pathname.startsWith("/service-admin/firmware");

  const isOnAdminUsersPage =
    location.pathname === "/admin" || location.pathname === "/admin/";
  const isOnAdminLogsPage = location.pathname.startsWith("/admin/logs");

  const isOnServiceAdminUsersPage =
    location.pathname === "/service-admin" ||
    location.pathname === "/service-admin/";
  const isOnServiceAdminLogsPage = location.pathname.startsWith(
    "/service-admin/logs",
  );

  const wrapTextWithSpans = (text, animKey) => {
    const safeText = String(text || "");

    if (isMobile) {
      return safeText;
    }

    return safeText.split("").map((char, index) => (
      <span
        key={`${animKey}-${index}`}
        style={{ "--i": index }}
        className={animatingLinks[animKey] ? "wave-animated" : ""}
      >
        {char === " " ? "\u00A0" : char}
      </span>
    ));
  };

  const startAnimation = (animKey) => {
    if (isMobile) return;
    if (animatingLinks[animKey]) return;

    setAnimatingLinks((prev) => ({
      ...prev,
      [animKey]: true,
    }));
  };

  const stopAnimation = (animKey) => {
    setAnimatingLinks((prev) => ({
      ...prev,
      [animKey]: false,
    }));
  };

  const firmwareLinkTo = capFirmwareManage
    ? "/service-admin/firmware"
    : "/firmware";

  const currentLanguage = i18n.resolvedLanguage === "en" ? "en" : "cs";

  const showMobileLoginLink =
    showPublicNav &&
    isMobile &&
    !isOnHomePage &&
    !isOnLoginPage &&
    !isOnRegisteredPage;

  const showMobileRegisterLink =
    showPublicNav &&
    isMobile &&
    !isOnHomePage &&
    !isOnRegisterPage &&
    !isOnRegisteredPage;

  const showDesktopPublicNav = showPublicNav && !isMobile && !isOnHomePage;

  const showLogoutOnly =
    !isResolvingAuth &&
    !!currentUser &&
    !showAppNav &&
    !showPublicNav &&
    !isPublicRoute;

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <header className="header notranslate" translate="no">
      <div className="header-left">
        <a
          href="https://www.codaco.cz"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="/logo-codaco.png" alt="Codaco logo" className="logo" />
        </a>
      </div>

      <nav className="header-nav notranslate" translate="no">
        {!isResolvingAuth && (
          <>
            {showDesktopPublicNav && (
              <>
                {!isOnLoginPage && !isOnRegisteredPage && (
                  <Link
                    to="/login"
                    className="header-text-link header-text-link--public"
                    translate="no"
                  >
                    {t(["header.login", "login"])}
                  </Link>
                )}

                {!isOnRegisterPage && !isOnRegisteredPage && (
                  <Link
                    to="/register"
                    className="header-text-link header-text-link--public"
                    translate="no"
                  >
                    {t(["header.register", "register"])}
                  </Link>
                )}
              </>
            )}

            {showMobileLoginLink && (
              <Link
                to="/login"
                className="header-text-link header-text-link--public"
                translate="no"
              >
                {t(["header.login", "login"])}
              </Link>
            )}

            {showMobileRegisterLink && (
              <Link
                to="/register"
                className="header-text-link header-text-link--public"
                translate="no"
              >
                {t(["header.register", "register"])}
              </Link>
            )}

            {showAppNav && (
              <>
                {capClientDocs && !isOnDashboardPage && (
                  <>
                    {isMobile ? (
                      <Link
                        to="/dashboard"
                        className="admin-link admin-icon-link"
                        title={t(["header.documents", "documents"])}
                        aria-label={t(["header.documents", "documents"])}
                      >
                        <FaFileAlt />
                      </Link>
                    ) : (
                      <Link
                        to="/dashboard"
                        className="admin-link header-text-link"
                        onMouseEnter={() => startAnimation("documents")}
                        onAnimationEnd={() => stopAnimation("documents")}
                        translate="no"
                      >
                        {wrapTextWithSpans(
                          t(["header.documents", "documents"]),
                          "documents",
                        )}
                      </Link>
                    )}
                  </>
                )}

                {capFirmwareRead && !isOnFirmwarePage && (
                  <>
                    {isMobile ? (
                      <Link
                        to={firmwareLinkTo}
                        className="admin-link admin-icon-link"
                        title={t(["header.firmware", "firmware"])}
                        aria-label={t(["header.firmware", "firmware"])}
                      >
                        <FaMicrochip />
                      </Link>
                    ) : (
                      <Link
                        to={firmwareLinkTo}
                        className="admin-link header-text-link"
                        onMouseEnter={() => startAnimation("firmware")}
                        onAnimationEnd={() => stopAnimation("firmware")}
                        translate="no"
                      >
                        {wrapTextWithSpans(
                          t(["header.firmware", "firmware"]),
                          "firmware",
                        )}
                      </Link>
                    )}
                  </>
                )}

                {capClientsManage && (
                  <>
                    {!isOnAdminUsersPage &&
                      (isMobile ? (
                        <Link
                          to="/admin"
                          className="admin-link admin-icon-link"
                          title={t(["header.clientAdmin", "client_admin"])}
                          aria-label={t(["header.clientAdmin", "client_admin"])}
                        >
                          <FaUsersCog />
                        </Link>
                      ) : (
                        <Link
                          to="/admin"
                          className="admin-link header-text-link"
                          onMouseEnter={() => startAnimation("clientAdmin")}
                          onAnimationEnd={() => stopAnimation("clientAdmin")}
                          translate="no"
                        >
                          {wrapTextWithSpans(
                            t(["header.clientAdmin", "client_admin"]),
                            "clientAdmin",
                          )}
                        </Link>
                      ))}

                    {!isOnAdminLogsPage &&
                      (isMobile ? (
                        <Link
                          to="/admin/logs"
                          className="admin-link admin-icon-link"
                          title={t(["header.clientLogs", "client_logs"])}
                          aria-label={t(["header.clientLogs", "client_logs"])}
                        >
                          <FaClipboardList />
                        </Link>
                      ) : (
                        <Link
                          to="/admin/logs"
                          className="admin-link header-text-link"
                          onMouseEnter={() => startAnimation("clientLogs")}
                          onAnimationEnd={() => stopAnimation("clientLogs")}
                          translate="no"
                        >
                          {wrapTextWithSpans(
                            t(["header.clientLogs", "client_logs"]),
                            "clientLogs",
                          )}
                        </Link>
                      ))}
                  </>
                )}

                {capFirmwareManage && (
                  <>
                    {!isOnServiceAdminUsersPage &&
                      (isMobile ? (
                        <Link
                          to="/service-admin"
                          className="admin-link admin-icon-link"
                          title={t(["header.serviceAdmin", "service_admin"])}
                          aria-label={t([
                            "header.serviceAdmin",
                            "service_admin",
                          ])}
                        >
                          <FaUsersCog />
                        </Link>
                      ) : (
                        <Link
                          to="/service-admin"
                          className="admin-link header-text-link"
                          onMouseEnter={() => startAnimation("serviceAdmin")}
                          onAnimationEnd={() => stopAnimation("serviceAdmin")}
                          translate="no"
                        >
                          {wrapTextWithSpans(
                            t(["header.serviceAdmin", "service_admin"]),
                            "serviceAdmin",
                          )}
                        </Link>
                      ))}

                    {!isOnServiceAdminLogsPage &&
                      (isMobile ? (
                        <Link
                          to="/service-admin/logs"
                          className="admin-link admin-icon-link"
                          title={t(["header.serviceLogs", "service_logs"])}
                          aria-label={t(["header.serviceLogs", "service_logs"])}
                        >
                          <FaClipboardList />
                        </Link>
                      ) : (
                        <Link
                          to="/service-admin/logs"
                          className="admin-link header-text-link"
                          onMouseEnter={() => startAnimation("serviceLogs")}
                          onAnimationEnd={() => stopAnimation("serviceLogs")}
                          translate="no"
                        >
                          {wrapTextWithSpans(
                            t(["header.serviceLogs", "service_logs"]),
                            "serviceLogs",
                          )}
                        </Link>
                      ))}
                  </>
                )}

                <button
                  type="button"
                  className="logout-button logout-button-icon"
                  onClick={handleLogout}
                  title={t(["header.logout", "logout"])}
                  aria-label={t(["header.logout", "logout"])}
                >
                  <FaSignOutAlt />
                </button>
              </>
            )}

            {showLogoutOnly && (
              <button
                type="button"
                className="logout-button logout-button-icon"
                onClick={handleLogout}
                title={t(["header.logout", "logout"])}
                aria-label={t(["header.logout", "logout"])}
              >
                <FaSignOutAlt />
              </button>
            )}
          </>
        )}

        <div
          className="lang-switcher-container notranslate"
          translate="no"
          aria-label="Language switcher"
        >
          <button
            type="button"
            onClick={() => handleLanguageChange("cs")}
            className={`lang-switch ${currentLanguage === "cs" ? "active" : ""}`}
            aria-pressed={currentLanguage === "cs"}
            lang="cs"
            translate="no"
          >
            CZ
          </button>
          <span className="lang-separator" translate="no">
            |
          </span>
          <button
            type="button"
            onClick={() => handleLanguageChange("en")}
            className={`lang-switch ${currentLanguage === "en" ? "active" : ""}`}
            aria-pressed={currentLanguage === "en"}
            lang="en"
            translate="no"
          >
            EN
          </button>
        </div>
      </nav>
    </header>
  );
}
